/**
 * CatFightTheater — Three distinct visual experiences for Cat Fight mode.
 *
 * BOXING RING: Two fighters center stage. Dark cinematic. Ringside view.
 * LIVE STREAM: Twitch-style vertical chat. Maximum density. Inline heckles.
 * WRESTLING:   Announcer spectacle. Dramatic entrances. Momentum bar.
 *
 * Receives all state from Theater.js parent — no SSE logic here, pure rendering.
 */
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Square, Pause, Play, Clock, Zap, ArrowRight, RefreshCw, Home } from 'lucide-react';
import { API } from '../App';
import VoiceAvatar from './VoiceAvatar';

// ============================================================================
// BOXING RING THEME
// ============================================================================
function BoxingRingTheater({ fray, messages, voices, activeVoice, currentPhase, tension,
  isStreaming, isPaused, elapsed, frayTotalCost, frayTotalTokens,
  onStop, onPause, onResume, onNavigateResults, frayId }) {

  const streamRef = useRef(null);
  useEffect(() => {
    streamRef.current?.scrollTo({ top: streamRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // Find the two most recently active voices for the "ring"
  const recentVoices = [];
  for (let i = messages.length - 1; i >= 0 && recentVoices.length < 2; i--) {
    const m = messages[i];
    if (m.type === 'message' && m.data?.voice && !recentVoices.includes(m.data.voice)) {
      recentVoices.push(m.data.voice);
    }
  }
  const fighter1 = voices.find(v => v.name === recentVoices[0]);
  const fighter2 = voices.find(v => v.name === recentVoices[1]);
  const cornerVoices = voices.filter(v => v.name !== recentVoices[0] && v.name !== recentVoices[1]);

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: 'radial-gradient(ellipse at center bottom, #1A0808 0%, #0A0505 40%, #050202 100%)' }}>
      {/* Ring Header — dark red glow */}
      <div className="h-14 flex items-center justify-between px-3 md:px-6 shrink-0"
        style={{ background: 'linear-gradient(to right, rgba(239,68,68,0.08), transparent, rgba(239,68,68,0.08))', borderBottom: '1px solid rgba(239,68,68,0.15)' }}>
        <div className="flex items-center gap-2 md:gap-4">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'}`}
              style={{ boxShadow: isPaused ? '0 0 8px rgba(234,179,8,0.5)' : '0 0 12px rgba(239,68,68,0.6)' }} />
            <span className="text-xs font-mono tracking-[0.2em] uppercase" style={{ color: isPaused ? '#EAB308' : '#EF4444' }}>
              {isPaused ? 'PAUSED' : 'LIVE'}
            </span>
          </div>
          {currentPhase && (
            <span className="hidden sm:inline text-sm font-bold tracking-wider text-white/60" style={{ fontFamily: "'Oswald', 'Impact', sans-serif" }}>
              {currentPhase.phase_name?.toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          <span className="text-xs font-mono text-white/30">{formatTime(elapsed)}</span>
          {frayTotalCost > 0 && <span className="hidden sm:inline text-[10px] font-mono text-red-400/60">${frayTotalCost.toFixed(4)}</span>}
          <FightControls isStreaming={isStreaming} isPaused={isPaused} onStop={onStop} onPause={onPause} onResume={onResume} onNavigateResults={onNavigateResults} hasVerdict={messages.some(m => m.type === 'verdict')} />
        </div>
      </div>

      {/* Ring Area — the fighters */}
      <div className="h-28 md:h-40 flex items-center justify-center gap-3 md:gap-8 px-3 md:px-8 shrink-0 relative">
        {/* Ropes effect */}
        <div className="absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(239,68,68,0.3), transparent)' }} />
        <div className="absolute inset-x-0 bottom-0 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(239,68,68,0.2), transparent)' }} />

        {/* Fighter 1 — Left corner */}
        <FighterCard voice={fighter1} isActive={activeVoice === fighter1?.name} side="left" />

        {/* VS badge */}
        <div className="flex flex-col items-center">
          <span className="text-3xl font-black text-red-500/80" style={{ fontFamily: "'Oswald', 'Impact', sans-serif", textShadow: '0 0 30px rgba(239,68,68,0.4)' }}>
            VS
          </span>
          <span className="text-[9px] font-mono text-white/20 mt-1">ROUND {currentPhase?.phase || '—'}</span>
        </div>

        {/* Fighter 2 — Right corner */}
        <FighterCard voice={fighter2} isActive={activeVoice === fighter2?.name} side="right" />

        {/* Corner voices */}
        <div className="absolute left-4 bottom-2 flex gap-2">
          {cornerVoices.map((v, i) => (
            <div key={i} className="flex items-center gap-1 px-2 py-1 bg-black/40 border border-white/5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: v.color || '#666', opacity: activeVoice === v.name ? 1 : 0.3 }} />
              <span className="text-[8px] font-mono text-white/30">{v.name}</span>
            </div>
          ))}
        </div>

        {/* Damage meter */}
        <div className="absolute right-4 bottom-2 w-32">
          <div className="flex items-center justify-between text-[8px] font-mono text-white/20 mb-1">
            <span>DAMAGE</span>
            <span>{Math.round(tension.score)}%</span>
          </div>
          <div className="h-1.5 bg-white/5 overflow-hidden">
            <motion.div className="h-full" animate={{ width: `${tension.score}%` }}
              style={{ background: tension.score > 70 ? 'linear-gradient(to right, #EF4444, #FF0000)' : 'linear-gradient(to right, #EF4444, #F59E0B)' }} />
          </div>
        </div>
      </div>

      {/* Fight Stream — scrollable messages */}
      <div ref={streamRef} className="flex-1 overflow-y-auto px-3 md:px-6 py-4" style={{ background: 'linear-gradient(to bottom, rgba(10,5,5,0), rgba(10,5,5,0.8))' }}>
        <AnimatePresence>
          {messages.map((msg, i) => {
            if (msg.type === 'message') {
              const voice = voices.find(v => v.name === msg.data.voice);
              const isHot = msg.data.moment_tags?.includes('hot_take') || msg.data.moment_tags?.includes('hot_moment');
              return (
                <motion.div key={`msg-${i}`} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                  className={`mb-4 p-4 border-l-3 ${isHot ? 'bg-red-500/5' : 'bg-white/[0.02]'}`}
                  style={{ borderLeftWidth: 3, borderLeftColor: voice?.color || '#EF4444', borderColor: isHot ? 'rgba(239,68,68,0.2)' : 'transparent' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <VoiceAvatar
                      name={msg.data.voice}
                      color={voice?.color || '#EF4444'}
                      emotion={isHot ? 'arguing' : 'speaking'}
                      alteredState={voice?.altered_state || null}
                      size={22}
                    />
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: voice?.color || '#EF4444', fontFamily: "'Oswald', sans-serif" }}>
                      {msg.data.voice}
                    </span>
                    {isHot && <span className="text-[9px] px-1.5 py-0.5 bg-red-500/20 text-red-400 font-mono">BODY BLOW</span>}
                  </div>
                  <p className="text-sm text-white/80 leading-relaxed">{msg.data.text}</p>
                </motion.div>
              );
            }
            if (msg.type === 'phase') {
              return (
                <motion.div key={`phase-${i}`} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                  className="my-8 text-center">
                  <div className="inline-block px-8 py-3" style={{ background: 'linear-gradient(to right, transparent, rgba(239,68,68,0.1), transparent)' }}>
                    <span className="text-[10px] font-mono text-red-500/40 block tracking-[0.3em]">ROUND</span>
                    <span className="text-4xl font-black text-red-500/60" style={{ fontFamily: "'Oswald', 'Impact', sans-serif" }}>{msg.data.phase}</span>
                  </div>
                </motion.div>
              );
            }
            if (msg.type === 'heckle') {
              return (
                <motion.div key={`heckle-${i}`} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}
                  className="my-2 flex items-start gap-2 px-4 py-2 bg-yellow-500/5 border-l-2 border-yellow-500/40">
                  <span className="text-sm">📣</span>
                  <div>
                    <span className="text-[9px] font-mono text-yellow-500/60">{msg.data.author} {msg.data.target !== 'all' ? `→ ${msg.data.target}` : '→ CROWD'}</span>
                    <p className="text-sm text-yellow-200/80">"{msg.data.text}"</p>
                  </div>
                </motion.div>
              );
            }
            if (msg.type === 'hot_moment') {
              return (
                <motion.div key={`hot-${i}`} initial={{ opacity: 0, scale: 1.3 }} animate={{ opacity: 1, scale: 1 }}
                  className="my-1 text-center text-[10px] font-mono text-red-400/60">
                  💥 {msg.data.voice} lands a devastating blow
                </motion.div>
              );
            }
            return null;
          })}
        </AnimatePresence>
      </div>

      {/* Heckle Bar */}
      <CatFightHeckleBar frayId={frayId} voices={voices} isStreaming={isStreaming} isPaused={isPaused}
        accentColor="#EF4444" placeholder="Shout from ringside..." buttonText="SHOUT" />
    </div>
  );
}

function FighterCard({ voice, isActive, side }) {
  if (!voice) return (
    <div className="w-28 h-20 md:w-44 md:h-28 border border-white/5 bg-white/[0.01] flex items-center justify-center">
      <span className="text-[10px] font-mono text-white/10">AWAITING</span>
    </div>
  );

  return (
    <motion.div
      animate={{ scale: isActive ? 1.05 : 0.95, opacity: isActive ? 1 : 0.5 }}
      transition={{ duration: 0.3 }}
      className="w-28 h-20 md:w-44 md:h-28 border p-2 md:p-3 relative overflow-hidden"
      style={{
        borderColor: isActive ? voice.color : 'rgba(255,255,255,0.05)',
        boxShadow: isActive ? `0 0 30px ${voice.color}30, inset 0 0 20px ${voice.color}10` : 'none',
        background: isActive ? `linear-gradient(135deg, ${voice.color}08, transparent)` : 'rgba(255,255,255,0.01)',
      }}>
      {isActive && (
        <motion.div className="absolute inset-0" animate={{ opacity: [0.05, 0.15, 0.05] }} transition={{ duration: 2, repeat: Infinity }}
          style={{ background: `radial-gradient(circle at ${side === 'left' ? '80%' : '20%'} 50%, ${voice.color}20, transparent 70%)` }} />
      )}
      <div className="relative z-10">
        <VoiceAvatar
          name={voice.name}
          color={voice.color}
          emotion={isActive ? 'arguing' : 'idle'}
          alteredState={voice.altered_state || null}
          size={window.innerWidth < 768 ? 32 : 44}
        />
        <span className="text-xs font-bold uppercase tracking-wider block mt-1" style={{ color: voice.color, fontFamily: "'Oswald', sans-serif" }}>
          {voice.name}
        </span>
        <span className="text-[8px] font-mono text-white/20">{voice.default_model?.split('/').pop()}</span>
      </div>
    </motion.div>
  );
}


// ============================================================================
// LIVE STREAM THEME
// ============================================================================
function LiveStreamTheater({ fray, messages, voices, activeVoice, currentPhase, tension,
  isStreaming, isPaused, elapsed, frayTotalCost, frayTotalTokens,
  onStop, onPause, onResume, onNavigateResults, frayId }) {

  const chatRef = useRef(null);
  const [reactions, setReactions] = useState([]);
  let reactionId = useRef(0);

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const addReaction = (emoji) => {
    const id = reactionId.current++;
    setReactions(prev => [...prev, { id, emoji, x: 70 + Math.random() * 20, created: Date.now() }]);
    setTimeout(() => setReactions(prev => prev.filter(r => r.id !== id)), 2000);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: '#0D0B1A' }}>
      {/* Stream Header — purple neon */}
      <div className="h-10 flex items-center justify-between px-4 shrink-0"
        style={{ background: 'linear-gradient(to right, rgba(168,85,247,0.05), rgba(168,85,247,0.02))', borderBottom: '1px solid rgba(168,85,247,0.1)' }}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-purple-500'}`}
              style={{ boxShadow: isPaused ? '' : '0 0 6px rgba(168,85,247,0.6)' }} />
            <span className="text-[10px] font-mono text-purple-400/80">{isPaused ? 'PAUSED' : 'LIVE'}</span>
          </div>
          <span className="text-xs text-white/50">{fray.scenario?.slice(0, 60)}{fray.scenario?.length > 60 ? '...' : ''}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono text-white/20">{formatTime(elapsed)}</span>
          <span className="text-[10px] font-mono text-purple-400/40">R{currentPhase?.phase || '?'}</span>
          <FightControls isStreaming={isStreaming} isPaused={isPaused} onStop={onStop} onPause={onPause} onResume={onResume} onNavigateResults={onNavigateResults} hasVerdict={messages.some(m => m.type === 'verdict')} accent="#A855F7" />
        </div>
      </div>

      {/* Chat Stream — full width, no sidebars */}
      <div className="flex-1 flex relative">
        <div ref={chatRef} className="flex-1 overflow-y-auto px-4 py-2">
          {messages.map((msg, i) => {
            if (msg.type === 'message') {
              const voice = voices.find(v => v.name === msg.data.voice);
              const isHot = msg.data.moment_tags?.includes('hot_take') || msg.data.moment_tags?.includes('hot_moment');
              return (
                <motion.div key={`msg-${i}`} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.06 }}
                  className={`flex gap-2 py-1.5 px-2 ${isHot ? 'bg-purple-500/10' : 'hover:bg-white/[0.02]'}`}>
                  <span className="text-xs font-bold shrink-0 w-28 truncate text-right" style={{ color: voice?.color || '#A855F7' }}>
                    {msg.data.voice}
                  </span>
                  <p className="text-sm text-white/75 leading-snug flex-1">{msg.data.text}</p>
                  {isHot && <span className="text-xs shrink-0 animate-bounce">🔥</span>}
                </motion.div>
              );
            }
            if (msg.type === 'phase') {
              return (
                <div key={`phase-${i}`} className="text-center py-2 my-1" style={{ borderTop: '1px solid rgba(168,85,247,0.1)', borderBottom: '1px solid rgba(168,85,247,0.1)' }}>
                  <span className="text-[10px] font-mono text-purple-400/40 tracking-[0.2em]">{msg.data.phase_name?.toUpperCase()}</span>
                </div>
              );
            }
            if (msg.type === 'heckle') {
              return (
                <motion.div key={`heckle-${i}`} initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                  className="flex gap-2 py-1.5 px-2 bg-yellow-500/5">
                  <span className="text-xs font-bold shrink-0 w-28 truncate text-right text-yellow-400/80">
                    📢 {msg.data.author}
                  </span>
                  <p className="text-sm text-yellow-200/70 flex-1">{msg.data.text}</p>
                </motion.div>
              );
            }
            if (msg.type === 'hot_moment') {
              return (
                <div key={`hot-${i}`} className="text-center py-0.5 text-[9px] text-purple-300/40 font-mono">
                  ⚡ {msg.data.voice} goes OFF
                </div>
              );
            }
            return null;
          })}
        </div>

        {/* Floating reactions */}
        <div className="absolute right-4 bottom-16 pointer-events-none">
          <AnimatePresence>
            {reactions.map(r => (
              <motion.div key={r.id} initial={{ opacity: 1, y: 0 }} animate={{ opacity: 0, y: -120 }}
                exit={{ opacity: 0 }} transition={{ duration: 2 }}
                className="absolute text-2xl" style={{ right: `${r.x - 70}%` }}>
                {r.emoji}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Reaction buttons — right side floating */}
        <div className="absolute right-3 bottom-4 flex flex-col gap-1">
          {['🔥', '💀', '🍿', '😂', '🤯'].map(emoji => (
            <button key={emoji} onClick={() => addReaction(emoji)}
              className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/5 rounded-full text-sm transition-colors">
              {emoji}
            </button>
          ))}
        </div>
      </div>

      {/* Stream Heckle Bar */}
      <CatFightHeckleBar frayId={frayId} voices={voices} isStreaming={isStreaming} isPaused={isPaused}
        accentColor="#A855F7" placeholder="Type in chat..." buttonText="SEND"
        style={{ background: '#0D0B1A', borderColor: 'rgba(168,85,247,0.1)' }} />
    </div>
  );
}


// ============================================================================
// WRESTLING THEME
// ============================================================================
function WrestlingTheater({ fray, messages, voices, activeVoice, currentPhase, tension,
  isStreaming, isPaused, elapsed, frayTotalCost, frayTotalTokens,
  onStop, onPause, onResume, onNavigateResults, frayId }) {

  const streamRef = useRef(null);
  useEffect(() => {
    streamRef.current?.scrollTo({ top: streamRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: 'linear-gradient(180deg, #0F0A00 0%, #0A0700 100%)' }}>
      {/* Announcer Header — gold & black */}
      <div className="h-16 flex items-center justify-between px-3 md:px-6 shrink-0 relative overflow-hidden"
        style={{ background: 'linear-gradient(to right, rgba(245,158,11,0.03), rgba(245,158,11,0.08), rgba(245,158,11,0.03))', borderBottom: '2px solid rgba(245,158,11,0.2)' }}>
        {/* Gold shimmer effect */}
        <motion.div className="absolute inset-0 pointer-events-none"
          animate={{ x: ['-100%', '200%'] }} transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
          style={{ background: 'linear-gradient(90deg, transparent, rgba(245,158,11,0.05), transparent)', width: '50%' }} />

        <div className="flex items-center gap-4 relative z-10">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 ${isPaused ? 'bg-yellow-600' : 'bg-amber-500 animate-pulse'}`}
              style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)', boxShadow: '0 0 10px rgba(245,158,11,0.4)' }} />
            <span className="text-xs tracking-[0.3em] uppercase font-bold"
              style={{ color: '#F59E0B', fontFamily: "'Oswald', 'Impact', sans-serif" }}>
              {isPaused ? 'INTERMISSION' : 'LIVE WRESTLING'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4 relative z-10">
          <span className="text-xs font-mono text-amber-500/30">{formatTime(elapsed)}</span>
          {/* Momentum bar */}
          <div className="w-24 h-3 bg-black/40 border border-amber-500/20 overflow-hidden relative">
            <motion.div className="h-full" animate={{ width: `${tension.score}%` }}
              style={{ background: `linear-gradient(to right, #F59E0B, ${tension.score > 70 ? '#EF4444' : '#F59E0B'})` }} />
            <span className="absolute inset-0 flex items-center justify-center text-[7px] font-mono font-bold text-black/60">MOMENTUM</span>
          </div>
          <FightControls isStreaming={isStreaming} isPaused={isPaused} onStop={onStop} onPause={onPause} onResume={onResume} onNavigateResults={onNavigateResults} hasVerdict={messages.some(m => m.type === 'verdict')} accent="#F59E0B" />
        </div>
      </div>

      {/* Fighter Roster — horizontal, styled as wrestler name plates */}
      <div className="h-16 flex items-center gap-2 px-3 md:px-6 shrink-0 overflow-x-auto"
        style={{ borderBottom: '1px solid rgba(245,158,11,0.1)' }}>
        {voices.map((voice, i) => {
          const isActive = activeVoice === voice.name;
          return (
            <motion.div key={i} animate={{ scale: isActive ? 1.05 : 1 }}
              className="flex items-center gap-2 px-3 py-1.5 relative"
              style={{
                background: isActive ? `linear-gradient(135deg, ${voice.color}15, transparent)` : 'transparent',
                borderLeft: `3px solid ${isActive ? voice.color : 'transparent'}`,
              }}>
              {isActive && (
                <motion.div className="absolute -left-1 top-0 bottom-0 w-1" animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1, repeat: Infinity }} style={{ background: voice.color }} />
              )}
              <span className="text-xs font-bold uppercase tracking-wider"
                style={{ color: isActive ? voice.color : 'rgba(255,255,255,0.2)', fontFamily: "'Oswald', sans-serif" }}>
                {voice.name}
              </span>
            </motion.div>
          );
        })}
      </div>

      {/* Wrestling Stream */}
      <div ref={streamRef} className="flex-1 overflow-y-auto px-3 md:px-6 py-4">
        <AnimatePresence>
          {messages.map((msg, i) => {
            if (msg.type === 'message') {
              const voice = voices.find(v => v.name === msg.data.voice);
              const isHot = msg.data.moment_tags?.includes('hot_take') || msg.data.moment_tags?.includes('hot_moment');

              return (
                <motion.div key={`msg-${i}`}
                  initial={{ opacity: 0, x: 80, rotateY: 15 }}
                  animate={{ opacity: 1, x: 0, rotateY: 0 }}
                  transition={{ duration: 0.4, type: 'spring', stiffness: 150, damping: 20 }}
                  className="mb-4">
                  {/* Wrestler name plate */}
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-px flex-1" style={{ background: `linear-gradient(to right, ${voice?.color || '#F59E0B'}40, transparent)` }} />
                    <span className="text-sm font-black uppercase tracking-[0.15em] px-3"
                      style={{ color: voice?.color || '#F59E0B', fontFamily: "'Oswald', 'Impact', sans-serif" }}>
                      {msg.data.voice}
                    </span>
                    <div className="h-px flex-1" style={{ background: `linear-gradient(to left, ${voice?.color || '#F59E0B'}40, transparent)` }} />
                  </div>

                  {/* Devastating overlay */}
                  {isHot && (
                    <motion.div initial={{ scale: 2, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                      className="text-center mb-2">
                      <span className="text-lg font-black text-amber-500 tracking-[0.2em]"
                        style={{ fontFamily: "'Oswald', 'Impact', sans-serif", textShadow: '0 0 20px rgba(245,158,11,0.4)' }}>
                        💥 DEVASTATING! 💥
                      </span>
                    </motion.div>
                  )}

                  <div className="p-4 bg-white/[0.02] border border-white/5"
                    style={{ borderLeftWidth: 3, borderLeftColor: voice?.color || '#F59E0B' }}>
                    <p className="text-sm text-white/75 leading-relaxed">{msg.data.text}</p>
                  </div>
                </motion.div>
              );
            }

            if (msg.type === 'phase') {
              return (
                <motion.div key={`phase-${i}`}
                  initial={{ opacity: 0, scale: 1.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, type: 'spring' }}
                  className="my-10 text-center relative py-6">
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, transparent, rgba(245,158,11,0.05), transparent)' }} />
                  <div className="relative z-10">
                    <span className="text-[11px] font-mono tracking-[0.5em] text-amber-500/30 block mb-2">AND NOW</span>
                    <span className="text-3xl font-black uppercase tracking-wider text-amber-500"
                      style={{ fontFamily: "'Oswald', 'Impact', sans-serif", textShadow: '0 0 40px rgba(245,158,11,0.3)' }}>
                      {msg.data.phase_name}
                    </span>
                    <span className="text-xs font-mono text-amber-500/20 block mt-2">— FIGHT! —</span>
                  </div>
                </motion.div>
              );
            }

            if (msg.type === 'heckle') {
              return (
                <motion.div key={`heckle-${i}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="my-3 text-center py-3 relative">
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, transparent, rgba(234,179,8,0.05), transparent)' }} />
                  <span className="text-[10px] font-mono tracking-[0.3em] text-yellow-500/40 block">THE CROWD ERUPTS</span>
                  <p className="text-sm text-yellow-200/70 mt-1">"{msg.data.text}"</p>
                  <span className="text-[9px] font-mono text-yellow-500/20">— {msg.data.author}</span>
                </motion.div>
              );
            }

            return null;
          })}
        </AnimatePresence>
      </div>

      {/* Wrestling Heckle Bar */}
      <CatFightHeckleBar frayId={frayId} voices={voices} isStreaming={isStreaming} isPaused={isPaused}
        accentColor="#F59E0B" placeholder="Announce to the arena..." buttonText="ANNOUNCE"
        style={{ background: '#0F0A00', borderColor: 'rgba(245,158,11,0.15)' }} />
    </div>
  );
}


// ============================================================================
// SHARED COMPONENTS
// ============================================================================

function FightControls({ isStreaming, isPaused, onStop, onPause, onResume, onNavigateResults, hasVerdict, accent = '#EF4444' }) {
  return (
    <div className="flex items-center gap-1.5">
      {isStreaming && !isPaused && (
        <>
          <button onClick={onPause} className="px-2 py-1 border text-[10px] font-mono transition-colors hover:bg-white/5"
            style={{ borderColor: 'rgba(234,179,8,0.3)', color: '#EAB308' }}>
            <Pause className="w-3 h-3 inline mr-1" />Pause
          </button>
          <button onClick={onStop} className="px-2 py-1 text-[10px] font-mono text-white transition-colors"
            style={{ background: accent }}>
            <Square className="w-3 h-3 inline mr-1" />Stop
          </button>
        </>
      )}
      {isPaused && (
        <>
          <button onClick={onResume} className="px-2 py-1 text-[10px] font-mono text-black bg-green-500">
            <Play className="w-3 h-3 inline mr-1" />Resume
          </button>
          <button onClick={onStop} className="px-2 py-1 text-[10px] font-mono text-white" style={{ background: accent }}>
            <Square className="w-3 h-3 inline mr-1" />Stop
          </button>
        </>
      )}
      {!isStreaming && !isPaused && hasVerdict && (
        <button onClick={onNavigateResults} className="px-3 py-1 text-[10px] font-mono text-black" style={{ background: accent }}>
          Results <ArrowRight className="w-3 h-3 inline ml-1" />
        </button>
      )}
    </div>
  );
}

function CatFightHeckleBar({ frayId, voices, isStreaming, isPaused, accentColor, placeholder, buttonText, style = {} }) {
  const [text, setText] = useState('');
  const [target, setTarget] = useState('all');
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState(0);

  const submitHeckle = async () => {
    if (!text.trim() || sending) return;
    if (Date.now() - lastSent < 10000) return;
    setSending(true);
    try {
      await API.post(`/api/frays/${frayId}/heckle`, { text: text.trim(), target });
      setText('');
      setLastSent(Date.now());
    } catch (err) {
      console.error('Heckle failed:', err);
    }
    setSending(false);
  };

  if (!isStreaming || isPaused) return null;

  return (
    <div className="min-h-[48px] flex flex-wrap sm:flex-nowrap items-center gap-2 px-3 md:px-4 py-2 sm:py-0 shrink-0"
      style={{ borderTop: `1px solid ${accentColor}20`, background: style.background || '#0F0F0F', ...style }}>
      <div className="flex items-center gap-1 shrink-0 overflow-x-auto">
        <button onClick={() => setTarget('all')}
          className="px-2 py-1 min-h-[36px] text-[8px] font-mono border transition-colors"
          style={target === 'all' ? { background: accentColor, color: '#000', borderColor: accentColor } : { borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' }}>
          ALL
        </button>
        {voices.slice(0, 4).map(v => (
          <button key={v.name} onClick={() => setTarget(v.name)}
            className="px-2 py-1 min-h-[36px] text-[8px] font-mono border transition-colors truncate max-w-[60px]"
            style={target === v.name ? { background: v.color, color: '#000', borderColor: v.color } : { borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' }}>
            {v.name}
          </button>
        ))}
      </div>
      <input value={text} onChange={e => setText(e.target.value.slice(0, 280))}
        onKeyDown={e => e.key === 'Enter' && submitHeckle()}
        className="flex-1 min-w-0 px-3 py-2 bg-transparent border text-sm focus:outline-none font-mono"
        style={{ borderColor: `${accentColor}20`, color: 'rgba(255,255,255,0.7)' }}
        placeholder={target === 'all' ? placeholder : `Call out ${target}...`}
        maxLength={280} />
      <button onClick={submitHeckle} disabled={sending || !text.trim() || Date.now() - lastSent < 10000}
        className="px-3 py-2 min-h-[44px] text-[10px] font-mono font-bold text-black disabled:opacity-20 transition-colors shrink-0"
        style={{ background: accentColor }}>
        {buttonText}
      </button>
    </div>
  );
}


// ============================================================================
// MAIN EXPORT — routes to the correct theme
// ============================================================================
export default function CatFightTheaterWrapper(props) {
  const theme = props.fray?.cat_fight_theme || 'boxing_ring';
  const { showStoppedOverlay, verdict, isStreaming, isPaused, onNavigateResults, onNavigateDashboard, onRerun } = props;

  // Auto-navigate to results when verdict arrives
  useEffect(() => {
    if (verdict && !isStreaming && !isPaused) {
      const timer = setTimeout(() => onNavigateResults?.(), 3000);
      return () => clearTimeout(timer);
    }
  }, [verdict, isStreaming, isPaused, onNavigateResults]);

  let TheaterComponent;
  switch (theme) {
    case 'live_stream':
      TheaterComponent = LiveStreamTheater; break;
    case 'wrestling':
      TheaterComponent = WrestlingTheater; break;
    case 'boxing_ring':
    default:
      TheaterComponent = BoxingRingTheater; break;
  }

  const accentColors = { boxing_ring: '#EF4444', live_stream: '#A855F7', wrestling: '#F59E0B' };
  const accent = accentColors[theme] || '#EF4444';

  return (
    <div className="relative h-screen">
      <TheaterComponent {...props} />

      {/* Stopped Overlay */}
      {showStoppedOverlay && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-50">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md mx-4 p-8 text-center border"
            style={{ background: '#1A1A1A', borderColor: accent + '40' }}>
            <Square className="w-10 h-10 mx-auto mb-3" style={{ color: accent }} />
            <h2 className="text-xl font-bold font-['Barlow_Condensed'] mb-2">Fight Over</h2>
            <p className="text-sm text-[#A0A0A0] mb-6">The Cat Fight has been stopped.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={onNavigateResults}
                className="px-6 py-2.5 text-sm font-semibold text-black flex items-center gap-2"
                style={{ background: accent }}>
                <ArrowRight className="w-4 h-4" /> View Fight Card
              </button>
              <button onClick={onRerun}
                className="px-6 py-2.5 text-sm border flex items-center gap-2"
                style={{ borderColor: accent + '40', color: accent }}>
                <RefreshCw className="w-4 h-4" /> Rematch
              </button>
              <button onClick={onNavigateDashboard}
                className="px-6 py-2.5 text-sm border border-[#2A2A2A] text-[#A0A0A0] flex items-center gap-2">
                <Home className="w-4 h-4" /> Dashboard
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Verdict / Completion Overlay */}
      {verdict && !showStoppedOverlay && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-50">
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="w-full max-w-md mx-4 p-8 text-center border-2"
            style={{ background: '#1A1A1A', borderColor: accent }}>
            <span className="text-[10px] font-mono tracking-[0.3em] block mb-2" style={{ color: accent }}>
              {verdict.decision === 'knockout' ? 'KNOCKOUT!' : verdict.decision === 'unanimous' ? 'UNANIMOUS!' : verdict.decision === 'draw' ? 'DRAW' : 'FIGHT OVER'}
            </span>
            <h2 className="text-3xl font-black font-['Barlow_Condensed'] mb-2">
              {verdict.winner || 'Draw'}
            </h2>
            {verdict.fight_summary && (
              <p className="text-sm text-[#A0A0A0] mb-4">{verdict.fight_summary}</p>
            )}
            <p className="text-[10px] font-mono text-[#A0A0A0] mb-4">Redirecting to results...</p>
            <button onClick={onNavigateResults}
              className="px-6 py-2.5 text-sm font-semibold text-black"
              style={{ background: accent }}>
              View Fight Card →
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
